import { ApolloError } from "@apollo/client";
import { AIAssessmentsList, Typography, useAssessmentStore } from "@cpj-ai/panel-npm-package";
import { GetAiAssessmentsQuery, ResumeAssessmentsReadyEnum } from "@graphql/generated";
import { useProfile } from "@hooks/useProfile";
import { UploadResumeFile } from "@modules/Resume/components/UploadResumeFile";
import { useState } from "react";

interface IExamsListProps {
  assessmentsData?: GetAiAssessmentsQuery;
  assessmentsLoading: boolean;
  assessmentsError: ApolloError | undefined;
  onAssessmentEnd?: () => void;
  refetchAssessments?: () => Promise<void>;
}

const ExamsList = ({
  assessmentsData,
  assessmentsLoading,
  assessmentsError,
  onAssessmentEnd,
  refetchAssessments,
}: IExamsListProps) => {
  const [isResumeUploaded, setIsResumeUploaded] = useState(false);
  const selectedExamInfo = useAssessmentStore((state) => state.selectedExamInfo);
  const { profile } = useProfile();

  const assessmentsNotFoundBottomComponent = () => {
    if (!profile.resumeAssessmentsReady) return null;
    else if (profile.resumeAssessmentsReady === ResumeAssessmentsReadyEnum.ASSESSMENTS_IN_PROGRESS) {
      return (
        <div className="mb-4 p-3 bg-purple-50 rounded-lg border mt-8 border-purple-200 animate-pulse">
          <div className="flex flex-col justify-center items-center">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-purple-600 text-sm">⏳</span>{" "}
              <Typography className="text-purple-700 font-medium text-xs">Creating your unique assessments</Typography>
            </div>

            <Typography className="text-purple-600 text-xs max-w-lg leading-5">
              We're analyzing your uploaded resume to generate personalized assessments based on your skills and
              experience. Please reload the page in a few minutes and check again.
            </Typography>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col md:flex-row items-center gap-6 py-4 md:py-2 px-4 rounded-lg mt-8 border w-fit max-w-full bg-primary-100/50 border-primary-300">
        <p className="text-sm text-primary-600">
          To get started, upload your resume to create an AI-powered assessment
        </p>
        <UploadResumeFile
          buttonClassName="bg-primary-500 text-white"
          containerClassName="!mt-0"
          iconColor="fill-white"
          onUploadSuccess={() => setIsResumeUploaded(true)}
        />
      </div>
    );
  };

  return (
    <div className={`h-full w-full pe-2 pb-72  ${selectedExamInfo && "hidden lg:block"}`}>
      <AIAssessmentsList
        assessmentsData={assessmentsData}
        assessmentsLoading={assessmentsLoading}
        assessmentsError={assessmentsError}
        onAssessmentEnd={onAssessmentEnd}
        isClickableCard={true}
        isResumeUploaded={isResumeUploaded}
        assessmentsNotFoundBottomComponent={assessmentsNotFoundBottomComponent()}
        refetchAssessments={refetchAssessments}
      />
    </div>
  );
};

export default ExamsList;
